import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { BlackbaudRaisersEdgeIntegration } from '@feathr/blackbox';
import type { IIntegrationUIToggleSection } from '@feathr/components';
import { ConfirmModal, SyncRuleSection } from '@feathr/components';

import { BLACKBAUD_ICON } from '../../blackbaudIcon';
import {
  getActivityValues,
  stageUpsyncActivities,
} from '../../RaisersEdgeActivitiesTab/RaisersEdgeActivitiesTab.utils';
import { FORM_SUBMISSION_UPSYNC_KEY } from '../../raisersEdgeConfig';

interface IProps {
  integration: BlackbaudRaisersEdgeIntegration;
  /** The config's create-constituents section: the Form submissions switch. */
  section: IIntegrationUIToggleSection;
  /** Disables every toggle with this reason, e.g. while the page saves. */
  disabledReason?: string;
}

/**
 * Person upsync: the Form submissions switch that creates Blackbaud constituents from Feathr form
 * submissions. Turning it on or off asks for confirmation first. It stages `people` and
 * `people_from_form_submissions` together, because the backend reads both. Edits are staged for the
 * page's Save & Sync.
 */
function RaisersEdgePersonUpsync({
  disabledReason,
  integration,
  section,
}: Readonly<IProps>): JSX.Element {
  const { t } = useTranslation();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // Kept after close, so the modal's copy doesn't flip while it animates out.
  const [confirmValue, setConfirmValue] = useState(false);
  const values = getActivityValues({ upsync_activities: integration.get('upsync_activities') });
  const isAvailable = integration.get('person_upsync_available') === true;
  const unavailableReason = isAvailable
    ? undefined
    : t('Person upsync isn’t available for this Raiser’s Edge connection yet.');

  function handleChange(key: string, value: boolean): void {
    if (key !== FORM_SUBMISSION_UPSYNC_KEY) {
      return;
    }
    setConfirmValue(value);
    setIsConfirmOpen(true);
  }

  function handleClose(): void {
    setIsConfirmOpen(false);
  }

  function handleConfirm(): void {
    stageUpsyncActivities(integration, {
      people: confirmValue,
      people_from_form_submissions: confirmValue,
    });
    setIsConfirmOpen(false);
  }

  return (
    <>
      <SyncRuleSection
        crmIcon={BLACKBAUD_ICON}
        disabledReason={disabledReason ?? unavailableReason}
        name={`raisers-edge-${section.key}`}
        onChange={handleChange}
        section={section}
        values={values}
      />
      <ConfirmModal
        confirmButtonText={confirmValue ? t('Turn on') : t('Turn off')}
        confirmButtonType={confirmValue ? 'primary' : 'danger'}
        description={
          confirmValue
            ? t(
                'Feathr will create a Blackbaud constituent for each new person who submits a Feathr form, with their first name, last name and email. A person with no email or no last name is not created. This doesn’t change existing constituents. Save & Sync to apply it.',
              )
            : t(
                'Feathr will stop creating Blackbaud constituents. Constituents already created stay in Blackbaud. Save & Sync to apply it.',
              )
        }
        onClose={handleClose}
        onConfirm={handleConfirm}
        opened={isConfirmOpen}
        t={t}
        title={confirmValue ? t('Turn on Form submissions?') : t('Turn off Form submissions?')}
      />
    </>
  );
}

export default observer(RaisersEdgePersonUpsync);
